<template>
  <div class="mx-10 ma-5">
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="errors = null" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-card flat class="pa-5">
      <div>
        <PageTitleWithSearch
          :title="'Invoices'"
          :btnName="'Create Invoice'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/invoice/create'"
          :btnPermission="'invoice.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        >
        </PageTitleWithSearch>
        <template v-if="invoices.length > 0 && hasPermission('invoice.list')">
          <DataTableWithPagination
            :headers="tableHeaders"
            :items="invoices"
            :loading="loading"
            :page="page"
            :total="total"
            @pageChange="page = $event"
          >
            <template #row="{ item, index }">
              <tr
                :class="[
                  { 'first-row': index === 0 },
                  index % 2 === 0 ? 'dark-row' : 'light-row',
                  'custom-row',
                ]"
                @click="goToItemList(item)"
              >
                <td class="text-left">{{ item.invoice_number }}</td>
                <td class="text-left">
                  {{ formatDate(item.invoice_date) }}
                </td>
                <td class="text-left">
                  {{ item.branch.name }}
                </td>
                <td class="text-left">
                  {{ item.customer.customer_code }} /
                  {{ item.customer.first_name }} {{ item.customer.last_name }}
                </td>
                <td class="text-left">
                  {{ Number(item.total_discount_amount).toFixed(2) }}
                </td>
                <td class="text-left">
                  {{ Number(item.total_tax_amount).toFixed(2) }}
                </td>
                <td class="text-left">
                  {{ Number(item.total_invoice).toFixed(2) }}
                </td>
                <td class="text-center" style="width: 50px;">
                  <v-btn
                    v-if="item.is_post == 0 && hasPermission('invoice.post')"
                    icon
                    small
                    color="#1976D2"
                    @click="postInvoice(item.uuid)"
                    style="min-width: 32px; height: 32px; box-shadow: none;"
                  >
                    Post
                  </v-btn>
                  <v-btn
                    v-else
                    icon
                    small
                    color="#1976D2"
                    disabled
                    style="min-width: 32px; height: 32px; box-shadow: none;"
                    >Posted</v-btn
                  >
                </td>
                <td class="text-center">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        class="mx-2"
                        small
                        style="box-shadow: none;"
                      >
                        <v-icon small>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item
                        v-if="
                          hasPermission('invoice.pay') &&
                            item.is_post == 1 &&
                            (item.receipt_amount_sum == null ||
                              Number(item.receipt_amount_sum) <
                                Number(item.total_invoice))
                        "
                        :disabled="item.is_post == 0"
                        @click="
                          item.is_post == 1 &&
                            (item.receipt_amount_sum == null ||
                            Number(item.receipt_amount_sum) <
                              Number(item.total_invoice)
                              ? openPayInvoice(item)
                              : null)
                        "
                      >
                        <v-list-item-icon>
                          <v-icon small color="#43A047">mdi-cash</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>
                          Pay Invoice
                          <span
                            v-if="item.is_post == 0"
                            style="color: #F44336; font-size: 12px; margin-left: 8px;"
                          >
                            (Post invoice before pay)
                          </span>
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="hasPermission('invoice.view')"
                        @click="goToItemList(item)"
                      >
                        <v-list-item-icon>
                          <v-icon small>mdi-eye</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>View</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="hasPermission('invoice.print')"
                        @click="printInvoice(item)"
                      >
                        <v-list-item-icon>
                          <v-icon small color="#1976D2">mdi-printer</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Print</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="
                          hasPermission('invoice.edit') && item.is_post == 0
                        "
                        @click="goToEditInvoice(item)"
                      >
                        <v-list-item-icon>
                          <v-icon small color="#FFA000">mdi-pencil</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Edit</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="
                          hasPermission('invoice.delete') && item.is_post == 0
                        "
                        @click="dropMemberConfirm(item.uuid)"
                      >
                        <v-list-item-icon>
                          <v-icon small color="#F44336">mdi-delete</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </template>
          </DataTableWithPagination>
        </template>
        <template v-else>
          <v-alert type="info" class="mt-5" outlined>
            No invoices found for the applied filters.
          </v-alert>
        </template>
      </div>
      <v-dialog v-model="payDialog" max-width="500px">
        <v-card>
          <v-card-title>
            Pay Invoice
          </v-card-title>
          <v-card-text>
            <v-form ref="payForm" v-model="payFormValid">
              <v-row>
                <v-col cols="12">
                  <strong>Invoice Number:</strong>
                  {{ payInvoiceData.invoice_number }}
                </v-col>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="payInvoiceData.payment_method_id"
                    :items="paymentOptions"
                    item-text="name"
                    item-value="id"
                    label="Payment Option"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="payInvoiceData.amount"
                    label="Amount"
                    type="number"
                    outlined
                    dense
                    required
                    :rules="[
                      (value) =>
                        Number(value) <= maxPayAmount ||
                        `Amount cannot exceed ${maxPayAmount}`,
                    ]"
                    :hint="`Max: ${maxPayAmount}`"
                    persistent-hint
                    @input="onPayAmountInput($event)"
                    @change="onPayAmountInput($event)"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="payInvoiceData.remark"
                    label="Remark"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="submitPayInvoice">Pay</v-btn>
            <v-btn text @click="payDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
    <ConfirmModal
      :text="'Do you want to drop this invoice?'"
      :title="'Drop Invoice'"
      :color="'red'"
      :confirmOpen="confirmOpen"
      @cancelConfirm="cancelConfirm()"
      @confirm="confirm()"
    ></ConfirmModal>
    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'warning'"
      @classSnackbar="closeSnackbar"
    />
  </div>
</template>

<style scoped></style>
<script>
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import { createNamespacedHelpers, mapGetters as rootMapGetters } from "vuex";
import { useDateFormatter } from "@/composables/useDateFormatter";
const { mapGetters } = createNamespacedHelpers("auth");
import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import Snackbars from "@/components/shared/Snackbars";
import { InvoiceService } from "@/services/invoice-service.js";
import { checkError } from "@/config/errors.js";
import ConfirmModal from "@/components/shared/ConfirmModal";

export default {
  name: "InvoiceGrid",
  components: {
    PageTitleWithSearch,
    Snackbars,
    ConfirmModal,
    DataTableWithPagination,
  },
  data: function() {
    return {
      invoices: [],
      page: 1,
      search: "",
      loading: false,
      errors: null,
      payFormValid: false,
      role: localStorage.getItem("role") || null,
      total: 0,
      totalInvoice: 0,
      dropId: null,
      confirmOpen: false,
      snackbar: false,
      successMsg: "Pdf not available",
      headers: [
        { text: "Invoice No", value: "invoice_number", align: "left" },
        { text: "Invoice Date", value: "invoice_date", align: "left" },
        { text: "Invoice Branch", value: "invoice_branch", align: "left" },
        { text: "Customer", value: "customer", align: "left" },
        { text: "Discount", value: "discount", align: "left", isCurrency: true },
        { text: "Tax", value: "tax", align: "left", isCurrency: true },
        { text: "Amount", value: "amount", align: "left", isCurrency: true },
        { text: "Post", value: "post", align: "left" },
        { text: "Actions", align: "center" },
      ],
      ...useDateFormatter(),
      payDialog: false,
      payInvoiceData: {
        invoice_id: null,
        invoice_number: "",
        payment_method_id: "",
        amount: "",
        remark: "",
      },
      paymentOptions: [
        { id: 1, name: "Cash" },
        { id: 2, name: "Card" },
        { id: 3, name: "Bank Transfer" },
      ],
      rules: {
        required: (value) => !!value || "This field is required.",
      },
    };
  },
  computed: {
    ...mapGetters({
      profile: "profile",
      hasPermission: "hasPermission",
    }),
    ...rootMapGetters("settings", ["dateFormat", "currencyCode"]),
    tableHeaders() {
      return this.headers.map((h) => {
        if (h.isCurrency) {
          return {
            ...h,
            text: `${h.text} (${this.currencyCode})`, // Add currency code automatically
          };
        }
        return h;
      });
    },
    maxPayAmount() {
      const invoice = this.invoices.find(
        (inv) => inv.id === this.payInvoiceData.invoice_id
      );
      if (!invoice) return 0;
      const total = Number(invoice.total_invoice) || 0;
      const paid = Number(invoice.receipt_amount_sum) || 0;
      return total - paid;
    },
  },
  mounted() {
    this.$store.dispatch("auth/getProfile");
    this.getInvoiceList();
  },

  watch: {
    search() {
      this.page = 1;
      this.getInvoiceList();
    },
    page() {
      this.getInvoiceList();
    },
  },

  methods: {
    goToItemList(item) {
      this.$router.push({
        name: "invoice-details",
        params: { uuid: item.uuid, invoice_number: item.id },
      });
    },

    getInvoiceList() {
      this.loading = true;
      InvoiceService.getInvoiceList(this.search, this.page)
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.invoices = response.data.data;
            this.total = response.data.meta.last_page || 0;
            this.totalInvoice = response.data.meta.total || 0;
          }
        })
        .catch((error) => {
          this.loading = false;
          this.invoices = [];
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    onPayAmountInput(value) {
      const max = this.maxPayAmount;
      if (!value || Number(value) < 0) {
        this.payInvoiceData.amount = 0;
      } else if (Number(value) > max) {
        this.payInvoiceData.amount = max;
      } else {
        this.payInvoiceData.amount = value;
      }
    },

    openPayInvoice(item) {
      this.payInvoiceData = {
        invoice_id: item.id,
        branch_id: item.branch_id,
        customer_id: item.customer_id,
        invoice_number: item.invoice_number,
        payment_method_id: "",
        amount: item.total_invoice - (item.receipt_amount_sum || 0),
        remark: "",
      };
      this.payDialog = true;
    },

    async submitPayInvoice() {
      if (!this.$refs.payForm.validate()) {
        this.successMsg = "Please fix validation errors before submitting.";
        this.snackbar = true;
        return;
      }
      const max = this.maxPayAmount;
      if (Number(this.payInvoiceData.amount) > max) {
        this.successMsg = `Amount cannot exceed ${max}`;
        this.snackbar = true;
        return;
      }
      try {
        const payload = {
          invoice_id: this.payInvoiceData.invoice_id,
          branch_id: this.payInvoiceData.branch_id,
          customer_id: this.payInvoiceData.customer_id,
          payment_method_id: this.payInvoiceData.payment_method_id,
          paymentAmount: this.payInvoiceData.amount,
          paymentRemark: this.payInvoiceData.remark,
        };
        await InvoiceService.createPayment(payload);
        this.payDialog = false;
        this.successMsg = "Payment successful!";
        this.snackbar = true;
        this.getInvoiceList();
      } catch (error) {
        this.snackbar = true;
        this.successMsg = "Payment failed!";
      }
    },

    searchChange(value) {
      this.search = value;
    },

    dropMemberConfirm(uuid) {
      this.dropId = uuid;
      this.confirmOpen = true;
    },

    cancelConfirm() {
      this.confirmOpen = false;
    },

    confirm() {
      this.confirmOpen = false;
      this.dropMember();
    },

    dropMember() {
      if (this.dropId) {
        this.loading = true;
        InvoiceService.dropMember(this.dropId)
          .then((response) => {
            if (response.data.message == "Invoice deleted successfully") {
              this.loading = false;
              this.successMsg = response.data.message;
              this.snackbarColor = "success";
              this.snackbar = true;
              this.getInvoiceList();
            } else {
              this.loading = false;
              this.snackbar = true;
            }
          })
          .catch((error) => {
            this.loading = false;
            this.snackbar = false;
            let data = error.response.data;
            let errorOutput = checkError.errorFormatting(data);
            this.errors = errorOutput;
          });
      }
    },
    goToEditInvoice(item) {
      this.$router.push({
        name: "EditInvoice",
        params: { uuid: item.uuid },
      });
    },

    async postInvoice(uuid) {
      this.successMsg = "";
      this.snackbar = false;
      this.errors = null;
      this.loading = true;
      try {
        const response = await InvoiceService.postInvoice(uuid);
        this.loading = false;
        this.successMsg = response.message || "Invoice posted successfully";
        this.snackbar = true;
        this.getInvoiceList();
      } catch (error) {
        this.loading = false;
        let data = error.response?.data;
        let errorOutput = checkError.errorFormatting(data);
        this.successMsg =
          errorOutput || (data?.message ?? "Failed to post invoice");
        this.snackbar = true;
      }
    },
    async printInvoice(item) {
      try {
        const response = await InvoiceService.getInvoiceDetails(item.uuid);
        const invoice = response.data.data;
        const items = invoice.invoice_items || [];

        let companyName = "";
        try {
          const companySettings = JSON.parse(localStorage.getItem("companySettings"));
          if (companySettings && Array.isArray(companySettings.data)) {
            const companyNameSetting = companySettings.data.find((s) => s.key === "company_name");
            if (companyNameSetting) {
              companyName = companyNameSetting.value;
            }
          }
        } catch (e) {
          // Ignore localStorage parse errors
        }

        let branchName = invoice.branch_name || "";
        let customerName = (invoice.customer && invoice.customer.company_name) || "";

        const invoiceDetails = `
          <div class="invoice-header" style="margin-bottom: 24px;">
            <table style="width:100%; border:none; margin-bottom:0; border:0;">
              <tr>
                <td style="width:60%; vertical-align:top; text-align:left; border:0;">
                  <div style="font-size:2.1em; font-weight:bold;">${companyName}</div>
                </td>
                <td style="width:40%; vertical-align:top; text-align:right; border:0;">
                  <div style="font-size:2.1em; font-weight:bold; letter-spacing:2px;">INVOICE</div>
                </td>
              </tr>
            </table>
            <table style="width:100%; border:none; margin-bottom:0; border:0;">
              <tr>
                <td style="width:50%; padding:4px 0; border:0;"><strong>Invoice Number:</strong> ${invoice.invoice_number || ""}</td>
                <td style="width:50%; padding:4px 0; border:0;"><strong>Invoice Date:</strong> ${invoice.invoice_date || ""}</td>
              </tr>
              <tr>
                <td style="width:50%; padding:4px 0; border:0;"><strong>Branch:</strong> ${branchName}</td>
                <td style="width:50%; padding:4px 0; border:0;"><strong>Customer:</strong> ${customerName}</td>
              </tr>
            </table>
          </div>
          <hr>
        `;

        const totalAmount = items.reduce((sum, i) => sum + Number(i.total_amount || 0), 0);
        const taxAmount = invoice.tax_amount || 0;
        const paidAmount = invoice.paid_amount || 0;
        const dueAmount = totalAmount + taxAmount - paidAmount;

        const invoiceFooter = `
          <div class="invoice-footer">
            <hr>
            <div style="display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; margin-top: 30px; gap: 24px;">
              <div style="flex: 1 1 340px; min-width: 320px; max-width: 60%;">
                <div style="margin-bottom:24px;">
                  <div style="margin-bottom:18px; display: flex; align-items: center; gap: 32px;">
                    <span><strong>Payment Type:</strong> <span style="display:inline-block; min-width:120px; border-bottom:1px solid #aaa;">&nbsp;</span></span>
                    <span><strong>Customer Signature:</strong> <span style="display:inline-block; min-width:120px; border-bottom:1px solid #aaa;">&nbsp;</span></span>
                  </div>
                  <div style="display: flex; align-items: center; gap: 32px;">
                    <span><strong>Biller</strong> <span style="display:inline-block; min-width:120px; border-bottom:1px solid #aaa;">&nbsp;</span></span>
                    <span><strong>Autoart Signature:</strong> <span style="display:inline-block; min-width:120px; border-bottom:1px solid #aaa;">&nbsp;</span></span>
                  </div>
                </div>
              </div>
              <div style="flex: 0 0 280px; max-width: 320px; min-width: 220px; align-self: flex-start; border:0;">
                <div style="border:0px solid #ccc; border-radius:8px; padding:0px 24px; width:100%; box-sizing:border-box;">
                  <div style="display:flex; justify-content:space-between; margin-bottom:8px;">
                    <span><strong>Invoice Total:</strong></span>
                    <span>${totalAmount.toFixed(2)}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; margin-bottom:8px;">
                    <span><strong>Tax:</strong></span>
                    <span>${taxAmount.toFixed(2)}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; margin-bottom:8px;">
                    <span><strong>Paid Amount:</strong></span>
                    <span>${paidAmount.toFixed(2)}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between;">
                    <span><strong>Due Amount:</strong></span>
                    <span>${dueAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;

        const tableHtml = `
          <table style="width:100%; border-collapse:collapse;">
            <thead>
              <tr>
                <th>Item Code</th>
                <th>Item Name</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Discount %</th>
                <th>Tax %</th>
                <th>Total Price</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(i => `
                <tr>
                  <td>${i.product_code}</td>
                  <td>${i.product_name}</td>
                  <td>${i.quantity}</td>
                  <td>${i.selling_price}</td>
                  <td>${i.discount}</td>
                  <td>${i.tax_percent || "-"}</td>
                  <td>${i.total_amount}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        `;

        const printWindow = window.open("", "_blank");
        printWindow.document.write(`
          <html>
            <head>
              <title>Print Invoice</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 40px; background: #f9f9f9; }
                .invoice-container { background: #fff; border: 1px solid #ddd; border-radius: 8px; padding: 32px; max-width: 800px; margin: auto; }
                .invoice-header { text-align: center; margin-bottom: 24px; }
                .invoice-header h1 { margin-bottom: 8px; font-size: 2.2em; letter-spacing: 2px; }
                .invoice-meta { text-align: left; margin-top: 16px; font-size: 1.1em; }
                hr { border: none; border-top: 1px solid #eee; margin: 24px 0; }
                table { width: 100%; border-collapse: collapse; margin-top: 16px; }
                th, td { border: 1px solid #ccc; padding: 10px; text-align: left; }
                th { background: #f5f5f5; }
                .invoice-footer { margin-top: 32px; }
                .v-data-footer { display: none !important; }
              </style>
            </head>
            <body>
              <div class="invoice-container">
                ${invoiceDetails}
                ${tableHtml}
                ${invoiceFooter}
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
      } catch (error) {
        this.snackbar = true;
        this.successMsg = "Unable to print invoice.";
      }
    },

    closeSnackbar() {
      this.snackbar = false;
    },
  },
};
</script>
